import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enBundle from '../locales/en.json';
import faBundle from '../locales/fa.json';
import {
  entriesToNamespaceResources,
  type PublishedLocaleBundle,
} from './localeBundle';

export type AppLocale = 'en' | 'fa';

export const SUPPORTED_LOCALES: AppLocale[] = ['en', 'fa'];
export const DEFAULT_LOCALE: AppLocale = 'en';
export const LOCALE_STORAGE_KEY = 'persianist_locale';
export const LOCALE_EVENT_NAME = 'persian-app:locale-changed';
/** Fired after a learner switches language, once the new language is on screen. */
export const LOCALE_USER_CHANGED_EVENT = 'persian-app:locale-user-changed';

export const BUNDLED_NAMESPACES = [
  'common',
  'auth',
  'settings',
  'notifications',
  'nav',
  'learning',
  'search',
  'keepLearning',
  'profile',
  'home',
  'subscription',
  'messages',
  'tour',
  'leitner',
  'wordHelp',
] as const;

const enPublished = enBundle as PublishedLocaleBundle;
const faPublished = faBundle as PublishedLocaleBundle;

/** Snapshot bundled with the app build (synced from wwwroot/i18n via Scripts/sync-i18n-to-client.mjs). */
export const BUNDLED_LOCALE_BUNDLES: Record<AppLocale, PublishedLocaleBundle> = {
  en: enPublished,
  fa: faPublished,
};

export const BUNDLED_VERSIONS: Record<AppLocale, string> = {
  en: enPublished.version ?? '0',
  fa: faPublished.version ?? '0',
};

const enPublishedNs = entriesToNamespaceResources(enPublished.entries ?? {});
const faPublishedNs = entriesToNamespaceResources(faPublished.entries ?? {});

const bundledResources = {
  en: enPublishedNs,
  fa: faPublishedNs,
};

export function normalizeLocale(value?: string | null): AppLocale {
  const v = (value ?? '').trim().toLowerCase();
  return v === 'fa' || v.startsWith('fa-') ? 'fa' : 'en';
}

export function isAdminPath(): boolean {
  return typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
}

export function localeDirection(locale: AppLocale): 'ltr' | 'rtl' {
  return locale === 'fa' ? 'rtl' : 'ltr';
}

/** Apply flat published translations (namespace.key → value) from language settings. */
export function applyServerTranslationOverrides(locale: AppLocale, entries: Record<string, string>): void {
  const byNs = entriesToNamespaceResources(entries);
  for (const [ns, keys] of Object.entries(byNs)) {
    i18n.addResourceBundle(locale, ns, keys, true, true);
  }
}

void i18n.use(initReactI18next).init({
  resources: bundledResources,
  lng: DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  defaultNS: 'common',
  ns: [...BUNDLED_NAMESPACES],
  interpolation: { escapeValue: false },
  returnEmptyString: false,
  react: { useSuspense: false },
});

export default i18n;
